import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { useTranslation } from 'react-i18next';
import Weather, {
  DailyForecast,
  HourlyForecast,
} from '../../../../interfaces/weather';
import { useService } from '../../../../services/services';
import DashboardLoading from '../../components/dashboard-loading';
import useGeoPosition from './use-geo-position';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDewpoint,
  faSnowflake,
  faTachometer,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBolt,
  faCloud,
  faCloudDrizzle,
  faClouds,
  faCloudsSun,
  faRaindrops,
  faSmog,
  faSun,
  faWind,
} from '@fortawesome/pro-solid-svg-icons';
import css from './index.module.css';

export default function MLWeatherWidget() {
  const { t } = useTranslation();
  const { weatherService } = useService();
  const [weather, setWeather] = useState(null);
  const [currentHour, setCurrentHour] = useState(1);
  const [shift, setShift] = useState(0);
  const [error, setError] = useState(null);
  const geoPosition = useGeoPosition();

  const getIcon = useCallback((id: number) => {
    // documentation: https://openweathermap.org/weather-conditions

    if (id > 802) return <FontAwesomeIcon icon={faClouds} />;
    if (id === 802) return <FontAwesomeIcon icon={faCloud} />;
    if (id === 801) return <FontAwesomeIcon icon={faCloudsSun} />;
    if (id === 800) return <FontAwesomeIcon icon={faSun} />;
    if (id >= 700) return <FontAwesomeIcon icon={faSmog} />;
    if (id >= 600) return <FontAwesomeIcon icon={faSnowflake} />;
    if (id >= 500) return <FontAwesomeIcon icon={faRaindrops} />;
    if (id >= 300) return <FontAwesomeIcon icon={faCloudDrizzle} />;
    if (id >= 200) return <FontAwesomeIcon icon={faBolt} />;
  }, []);

  const getCommonValue = useCallback((arr: any) => {
    return arr
      .filter((v: any) => v)
      .sort(
        (a: any, b: any) =>
          arr.filter((v: any) => v === a).length -
          arr.filter((v: any) => v === b).length
      )
      .pop();
  }, []);

  const prev = () => {
    if (currentHour > 1) {
      setCurrentHour((h) => h - 1);
      setShift((s) => s + 33.33);
    }
  };

  const next = () => {
    if (currentHour < weather.hourly?.length - 2) {
      setCurrentHour((h) => h + 1);
      setShift((s) => s - 33.33);
    }
  };

  useEffect(() => {
    if (!geoPosition) return;

    const subscription = weatherService
      .getData(geoPosition)
      .pipe(
        map((res: any) => {
          if (!(res && res.city && res.list && res.list.length)) {
            throw new Error('bad API response structure');
          }

          const [ch, h1, h2, h3, h4, h5, h6, h7] = res.list;
          const {
            dt_txt,
            wind: { speed },
            weather: [c],
            main: { temp, humidity, pressure },
          } = ch;
          const comingDayDates = res.list
            .map((item: any) =>
              moment(item.dt_txt, 'YYYY-MM-DD HH:mm:SS').format('YYYY-MM-DD')
            )
            .filter((v: any, i: any, a: any) => a.indexOf(v) === i)
            .splice(1, 4);
          const daily: DailyForecast[] = [];

          comingDayDates.forEach((date: string) => {
            const hours = res.list.filter((i: any) => i.dt_txt.includes(date));
            const night = hours.find((i: any) => i.dt_txt.includes('03:00:00'));
            const day = hours.find((i: any) => i.dt_txt.includes('15:00:00'));
            const weatherCodes = hours.map((i: any) => i.weather[0].id);
            const weather = getCommonValue(weatherCodes);

            daily.push({
              name: t(
                'pages.dashboard.weather.' +
                  moment(date).locale('en').format('dddd')
              ),
              day: Math.round(day.main.temp),
              night: Math.round(night.main.temp),
              weather: getIcon(weather),
            });
          });

          const result: Weather = {
            city: res.city.name,
            current: {
              speed,
              temp: Math.round(temp),
              humidity,
              pressure,
              weather: getIcon(c.id),
              description: c.description,
              time: moment(dt_txt, 'YYYY-MM-DD HH:mm:SS').format('HH:mm'),
            },
            hourly: [h1, h2, h3, h4, h5, h6, h7]
              .filter((h) => h)
              .map(({ dt_txt, main, weather }) => ({
                h: moment(dt_txt, 'YYYY-MM-DD HH:mm:SS').format('HH:mm'),
                t: Math.round(main.temp),
                w: getIcon(weather[0].id),
              })),
            daily: daily,
          };

          return result;
        }),
        catchError((error: any) => {
          setError(error);
          return of(null);
        })
      )
      .subscribe((w: Weather) => {
        if (w) setWeather(() => w);
      });

    return () => subscription.unsubscribe();
  }, [geoPosition, getCommonValue, getIcon, t, weatherService]);

  return (
    <div data-name="dashboard-weather">
      {!weather && !error ? (
        <DashboardLoading name="dashboard-weather-loading" />
      ) : error ? (
        <p>{t('pages.dashboard.weather.error')}</p>
      ) : (
        <div className={css['weather']} data-name="dashboard-weather-data">
          <div className={css['location']}>
            {weather.city}
            {/*<sup>{weather.current.time}</sup>*/}
          </div>
          <div className={css['current']}>
            <div className={css['temperature']}>{weather.current?.temp}°C</div>
            {weather.current?.weather}
            <div className={css['condition']}>
              {weather.current?.description}
            </div>
          </div>
          <div className={css['info']}>
            <div className={css['wind']}>
              <FontAwesomeIcon icon={faWind} />
              <span className={css['text']}>{weather.current?.speed} m/s</span>
            </div>
            <div className={css['humidity']}>
              <FontAwesomeIcon icon={faDewpoint} />
              <span className={css['text']}>{weather.current?.humidity} %</span>
            </div>
            <div className={css['pressure']}>
              <FontAwesomeIcon icon={faTachometer} />
              <span className={css['text']}>
                {weather.current?.pressure} <span>hPa</span>
              </span>
            </div>
          </div>
          <div className={css['delimiter']} />

          <div className={css['hours']}>
            <div className={css['slider']}>
              <div
                className={css['content']}
                style={{ transform: `translate(${shift}%, 0)` }}
              >
                {weather.hourly?.map((hourly: HourlyForecast, i: number) => (
                  <div className={css['hour']} key={`weather-hour-${i}`}>
                    <div className={css['date']}>{hourly.h}</div>
                    {hourly.w}
                    <div className={css['temperature']}>{hourly.t}°C</div>
                  </div>
                ))}
              </div>
            </div>
            <div
              className={`${css['arrow']} ${css['arrow-left']}`}
              onClick={prev}
            >
              <span>‹</span>
            </div>
            <div
              className={`${css['arrow']} ${css['arrow-right']}`}
              onClick={next}
            >
              <span>›</span>
            </div>
          </div>

          <div className={css['forecast']}>
            {weather.daily?.map((daily: DailyForecast, i: number) => (
              <div className={css['day-forecast']} key={`weather-day-${i}`}>
                <div className={css['date']}>{daily.name}</div>
                <div className={css['data']}>
                  {daily.weather}
                  <div className={css['temperature']}>
                    <div className={css['day']}>{daily.day}°C</div>
                    <div className={css['night']}>{daily.night}°C</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
